import AuthenticatedScrumbleApiClient from '@/repositories/clients/AuthenticatedScrumbleApiClient';
import PublicScrumbleApiClient from '@/repositories/clients/PublicScrumbleApiClient';
import ScrumbleApiClient from '@/repositories/clients/ScrumbleApiClient';
import ScrumbleAuthClient from '@/repositories/clients/ScrumbleAuthClient';
import axios from 'axios';
import AuthenticatedClient from './clients/AuthenticatedClient';
import Client from './clients/Client';

/**
 * Authentication repository (`/auth` + Scrumble OAuth/user endpoints).
 *
 * The auth-flow methods (register, authenticate, logout, webAuthn*, …) are
 * bound at module load to either the direct-Scrumble or backend-proxy
 * implementation based on `VUE_APP_AUTH_METHOD`; both implementations return
 * the raw AxiosResponse, so callers read `.data`. The common methods below
 * follow the same raw-response convention. Exception: `getAuthMethod()`
 * returns the resolved auth-method string synchronously (not a Promise).
 */
const resource = '/auth';
const authMethod = process.env.VUE_APP_AUTH_METHOD || 'direct';
const webAuthnRequestConfig = { withCredentials: true };

// Direct Scrumble authentication methods (OAuth2 flow like legacy)
const directScrumbleAuth = {
  register(payload) {
    return ScrumbleApiClient.post('user/register', payload);
  },
  refreshToken(refreshToken) {
    return ScrumbleAuthClient.post('oauth/token', {
      grant_type: 'refresh_token',
      refresh_token: refreshToken,
      client_id: process.env.VUE_APP_SCRUMBLE_CLIENT_ID,
      client_secret: process.env.VUE_APP_SCRUMBLE_CLIENT_SECRET,
      scope: '',
    });
  },
  authenticate(
    username,
    password,
    acceptTos,
    sms2faCode = null,
    totpCode = null,
    recoveryCode = null,
    email2faCode = null
  ) {
    return ScrumbleAuthClient.post('oauth/token', {
      grant_type: 'password',
      client_id: process.env.VUE_APP_SCRUMBLE_CLIENT_ID,
      client_secret: process.env.VUE_APP_SCRUMBLE_CLIENT_SECRET,
      username,
      password,
      accept_tos: acceptTos,
      sms_2fa_code: sms2faCode,
      totp_code: totpCode,
      recovery_code: recoveryCode,
      email_2fa_code: email2faCode,
    });
  },
  recoverPassword(payload) {
    return ScrumbleAuthClient.post('forgot-password', payload);
  },
  resetPassword(payload) {
    return ScrumbleAuthClient.post('reset-password', payload);
  },
  resendActivation(payload) {
    return PublicScrumbleApiClient.post('auth/resend-activation', payload);
  },
  activateSubscriptionWelcome(payload) {
    return PublicScrumbleApiClient.post('auth/subscription-welcome/activate', payload);
  },
  /** Sign in after the visitor's own guest checkout; 202 until the account exists. */
  claimCheckout(payload) {
    return PublicScrumbleApiClient.post('auth/checkout/claim', payload);
  },
  activateTenantInvitation(payload) {
    return PublicScrumbleApiClient.post('auth/tenant-invitation/activate', payload);
  },
  logout() {
    // Best-effort server-side revocation; useSessionStore.logoutUser() awaits
    // this before clearing local tokens, so it must never hang local sign-out.
    return AuthenticatedClient.get(`${resource}/logout`, { timeout: 5000 });
  },
  userInformation() {
    return AuthenticatedScrumbleApiClient.get('current-user');
  },
  sendSmsVerification(payload) {
    return AuthenticatedScrumbleApiClient.put('user/current-user/send-sms-verification', payload);
  },
  verifyPhone(payload) {
    return AuthenticatedScrumbleApiClient.put('user/current-user/verify-sms-code', payload);
  },
  send2fsSmsCode(payload) {
    return AuthenticatedScrumbleApiClient.post('auth/sms-2fa/send', payload);
  },
  send2faEmailCode(payload) {
    return AuthenticatedScrumbleApiClient.post('auth/email-2fa/send', payload);
  },
  webAuthnHasCredential(email) {
    return ScrumbleApiClient.post('auth/webauthn/has-credential', { email }, webAuthnRequestConfig);
  },
  webAuthnChallenge(email) {
    return ScrumbleApiClient.post('auth/webauthn/challenge', email ? { email } : {}, webAuthnRequestConfig);
  },
  webAuthnAuthenticate(assertion) {
    return ScrumbleApiClient.post('auth/webauthn/authenticate', assertion, webAuthnRequestConfig);
  },
  webAuthnRegisterOptions() {
    return AuthenticatedScrumbleApiClient.post('webauthn/register/options', {}, webAuthnRequestConfig);
  },
  webAuthnRegister(attestation) {
    return AuthenticatedScrumbleApiClient.post('webauthn/register', attestation, webAuthnRequestConfig);
  },
  webAuthnListCredentials() {
    return AuthenticatedScrumbleApiClient.get('webauthn/credentials', webAuthnRequestConfig);
  },
  webAuthnDeleteCredential(credentialId) {
    return AuthenticatedScrumbleApiClient.delete(`webauthn/credentials/${credentialId}`, webAuthnRequestConfig);
  },
  twoFactorStatus() {
    return AuthenticatedScrumbleApiClient.get('auth/two-factor');
  },
  enableTotp(payload) {
    return AuthenticatedScrumbleApiClient.post('auth/two-factor/totp', payload);
  },
  confirmTotp(payload) {
    return AuthenticatedScrumbleApiClient.post('auth/two-factor/totp/confirm', payload);
  },
  disableTotp(payload) {
    return AuthenticatedScrumbleApiClient.delete('auth/two-factor/totp', { data: payload });
  },
  showRecoveryCodes(payload) {
    return AuthenticatedScrumbleApiClient.post('auth/two-factor/recovery-codes', payload);
  },
  regenerateRecoveryCodes(payload) {
    return AuthenticatedScrumbleApiClient.post('auth/two-factor/recovery-codes/regenerate', payload);
  },
  enableSms(payload) {
    return AuthenticatedScrumbleApiClient.post('auth/two-factor/sms', payload);
  },
  disableSms(payload) {
    return AuthenticatedScrumbleApiClient.delete('auth/two-factor/sms', { data: payload });
  },
  enableEmail2fa(payload) {
    return AuthenticatedScrumbleApiClient.post('auth/two-factor/email', payload);
  },
  disableEmail2fa(payload) {
    return AuthenticatedScrumbleApiClient.delete('auth/two-factor/email', { data: payload });
  },
};

// Backend proxy authentication methods
const proxyAuth = {
  register(payload) {
    return Client.post(`${resource}/proxy/register`, payload);
  },
  refreshToken(refreshToken) {
    return Client.post(`${resource}/proxy/refresh`, {
      refresh_token: refreshToken,
    });
  },
  authenticate(
    username,
    password,
    acceptTos,
    sms2faCode = null,
    totpCode = null,
    recoveryCode = null,
    email2faCode = null
  ) {
    return Client.post(`${resource}/proxy/login`, {
      username,
      password,
      accept_tos: acceptTos,
      sms_2fa_code: sms2faCode,
      totp_code: totpCode,
      recovery_code: recoveryCode,
      email_2fa_code: email2faCode,
    });
  },
  recoverPassword(payload) {
    return Client.post(`${resource}/proxy/forgot-password`, payload);
  },
  resetPassword(payload) {
    return Client.post(`${resource}/proxy/reset-password`, payload);
  },
  resendActivation(payload) {
    return PublicScrumbleApiClient.post('auth/resend-activation', payload);
  },
  activateSubscriptionWelcome(payload) {
    return PublicScrumbleApiClient.post('auth/subscription-welcome/activate', payload);
  },
  /** Sign in after the visitor's own guest checkout; 202 until the account exists. */
  claimCheckout(payload) {
    return PublicScrumbleApiClient.post('auth/checkout/claim', payload);
  },
  activateTenantInvitation(payload) {
    return PublicScrumbleApiClient.post('auth/tenant-invitation/activate', payload);
  },
  logout() {
    // Best-effort server-side revocation; useSessionStore.logoutUser() awaits
    // this before clearing local tokens, so it must never hang local sign-out.
    return AuthenticatedClient.post(`${resource}/proxy/logout`, { timeout: 5000 });
  },
  userInformation() {
    return AuthenticatedClient.get(`${resource}/proxy/current-user`);
  },
  sendSmsVerification(payload) {
    return AuthenticatedClient.put(`${resource}/proxy/phone/send-verification`, payload);
  },
  verifyPhone(payload) {
    return AuthenticatedClient.put(`${resource}/proxy/phone/verify`, payload);
  },
  send2fsSmsCode(payload) {
    return Client.post(`${resource}/proxy/sms-2fa/send`, payload);
  },
  send2faEmailCode(payload) {
    return Client.post(`${resource}/proxy/email-2fa/send`, payload);
  },
  webAuthnHasCredential(email) {
    return Client.post(`${resource}/proxy/webauthn/has-credential`, { email }, webAuthnRequestConfig);
  },
  webAuthnChallenge(email) {
    return Client.post(`${resource}/proxy/webauthn/challenge`, email ? { email } : {}, webAuthnRequestConfig);
  },
  webAuthnAuthenticate(assertion) {
    return Client.post(`${resource}/proxy/webauthn/authenticate`, assertion, webAuthnRequestConfig);
  },
  webAuthnRegisterOptions() {
    return AuthenticatedClient.post(`${resource}/proxy/webauthn/register/options`, {}, webAuthnRequestConfig);
  },
  webAuthnRegister(attestation) {
    return AuthenticatedClient.post(`${resource}/proxy/webauthn/register`, attestation, webAuthnRequestConfig);
  },
  webAuthnListCredentials() {
    return AuthenticatedClient.get(`${resource}/proxy/webauthn/credentials`, webAuthnRequestConfig);
  },
  webAuthnDeleteCredential(credentialId) {
    return AuthenticatedClient.delete(`${resource}/proxy/webauthn/credentials/${credentialId}`, webAuthnRequestConfig);
  },
  twoFactorStatus() {
    return AuthenticatedClient.get(`${resource}/proxy/two-factor`);
  },
  enableTotp(payload) {
    return AuthenticatedClient.post(`${resource}/proxy/two-factor/totp`, payload);
  },
  confirmTotp(payload) {
    return AuthenticatedClient.post(`${resource}/proxy/two-factor/totp/confirm`, payload);
  },
  disableTotp(payload) {
    return AuthenticatedClient.delete(`${resource}/proxy/two-factor/totp`, { data: payload });
  },
  showRecoveryCodes(payload) {
    return AuthenticatedClient.post(`${resource}/proxy/two-factor/recovery-codes`, payload);
  },
  regenerateRecoveryCodes(payload) {
    return AuthenticatedClient.post(`${resource}/proxy/two-factor/recovery-codes/regenerate`, payload);
  },
  enableSms(payload) {
    return AuthenticatedClient.post(`${resource}/proxy/two-factor/sms`, payload);
  },
  disableSms(payload) {
    return AuthenticatedClient.delete(`${resource}/proxy/two-factor/sms`, { data: payload });
  },
  enableEmail2fa(payload) {
    return AuthenticatedClient.post(`${resource}/proxy/two-factor/email`, payload);
  },
  disableEmail2fa(payload) {
    return AuthenticatedClient.delete(`${resource}/proxy/two-factor/email`, { data: payload });
  },
};

// Select auth implementation based on environment variable
const authImpl = authMethod === 'direct' ? directScrumbleAuth : proxyAuth;

export default {
  /** Activate an account by POSTing to the provided activation URL. @param {string} url @returns {Promise<import('axios').AxiosResponse>} */
  activate(url, payload = {}) {
    return axios.post(url, payload);
  },
  // Auth method-specific implementations (raw AxiosResponse; read `.data`)
  reviewTenantInvitation(payload) {
    return PublicScrumbleApiClient.post('auth/tenant-invitation/review', payload);
  },
  register: authImpl.register,
  refreshToken: authImpl.refreshToken,
  authenticate: authImpl.authenticate,
  recoverPassword: authImpl.recoverPassword,
  resetPassword: authImpl.resetPassword,
  activateSubscriptionWelcome: authImpl.activateSubscriptionWelcome,
  claimCheckout: authImpl.claimCheckout,
  activateTenantInvitation: authImpl.activateTenantInvitation,
  resendActivation: authImpl.resendActivation,
  logout: authImpl.logout,
  userInformation: authImpl.userInformation,
  sendSmsVerification: authImpl.sendSmsVerification,
  verifyPhone: authImpl.verifyPhone,
  send2fsSmsCode: authImpl.send2fsSmsCode,
  send2faEmailCode: authImpl.send2faEmailCode,
  webAuthnHasCredential: authImpl.webAuthnHasCredential,
  webAuthnChallenge: authImpl.webAuthnChallenge,
  webAuthnAuthenticate: authImpl.webAuthnAuthenticate,
  webAuthnRegisterOptions: authImpl.webAuthnRegisterOptions,
  webAuthnRegister: authImpl.webAuthnRegister,
  webAuthnListCredentials: authImpl.webAuthnListCredentials,
  webAuthnDeleteCredential: authImpl.webAuthnDeleteCredential,
  twoFactorStatus: authImpl.twoFactorStatus,
  enableTotp: authImpl.enableTotp,
  confirmTotp: authImpl.confirmTotp,
  disableTotp: authImpl.disableTotp,
  showRecoveryCodes: authImpl.showRecoveryCodes,
  regenerateRecoveryCodes: authImpl.regenerateRecoveryCodes,
  enableSms: authImpl.enableSms,
  disableSms: authImpl.disableSms,
  // Common methods that don't change based on auth method (raw AxiosResponse; read `.data`)
  /** Mark the payment popup as seen for the current user. @returns {Promise<import('axios').AxiosResponse>} */
  markPaymentPopupAsSeen() {
    return AuthenticatedScrumbleApiClient.put('user/current-user/mark-payment-popup-seen');
  },
  /** Fetch the Stripe billing-portal URL. @returns {Promise<import('axios').AxiosResponse>} */
  billingPortalUrl() {
    return AuthenticatedScrumbleApiClient.get('stripe/billing-portal');
  },
  /** List products. @returns {Promise<import('axios').AxiosResponse>} */
  getProducts() {
    return AuthenticatedScrumbleApiClient.get('product');
  },
  /**
   * Public, unauthenticated live subscription catalog for the /subscriptions
   * page (plans, token/alert packages, plugin, tool add-ons, trial). Reflects
   * the PM's admin edits immediately. @returns {Promise<import('axios').AxiosResponse>}
   */
  getSubscriptionPlans() {
    return ScrumbleApiClient.get('subscription-plans');
  },
  /**
   * Start a public paid Stripe Checkout for a self-serve subscription.
   * Resolves to `{ url }` (redirect to Stripe) or a 409 with
   * `{ code: 'account_exists' }` when the email already has an account.
   * @param {{plan:string, interval:string, credit_pack?:string, alert_pack?:string, plugin?:boolean, tool_addons?:string[], email:string}} payload
   * @returns {Promise<import('axios').AxiosResponse>}
   */
  createGuestSubscriptionCheckout(payload) {
    return ScrumbleApiClient.post('subscription-checkout', payload);
  },
  /**
   * Start the selected-plan trial through card-required Stripe Checkout.
   * @param {{firstname:string, lastname:string, email:string, password:string, accept_tos:boolean, plan:'base'|'pro'}} payload
   * @returns {Promise<import('axios').AxiosResponse>}
   */
  startPublicComboTrial(payload) {
    return ScrumbleApiClient.post('trial/combo', payload);
  },
  /**
   * Check whether the authenticated user's account can start the Combo trial.
   * @returns {Promise<import('axios').AxiosResponse>}
   */
  getComboTrialEligibility() {
    return AuthenticatedScrumbleApiClient.get('user/trial/eligibility');
  },
  /**
   * Resolve the recommended self-serve upgrade offer for a missing entitlement.
   * @param {string} key entitlement lookup key
   * @returns {Promise<import('axios').AxiosResponse>}
   */
  getUpgradeOffer(key) {
    return AuthenticatedScrumbleApiClient.get('me/upgrade-offer', { params: { key } });
  },
  /**
   * Start the authenticated in-portal Combo trial.
   * @returns {Promise<import('axios').AxiosResponse>}
   */
  startAuthenticatedComboTrial(plan = 'pro') {
    return AuthenticatedScrumbleApiClient.post('user/trial/combo', { plan });
  },
  /**
   * Start an authenticated paid custom-build checkout.
   * @param {{plan:string, interval:string, credit_pack?:string, alert_pack?:string, plugin?:boolean, tool_addons?:string[], seat_count?:number}} payload
   * @returns {Promise<import('axios').AxiosResponse>}
   */
  createAuthenticatedSubscriptionCheckout(payload) {
    return AuthenticatedScrumbleApiClient.post('user/subscription-checkout', payload);
  },
  /** Probe a protected endpoint (auth check). @param {string} accessToken @returns {Promise<import('axios').AxiosResponse>} */
  protected(accessToken) {
    return AuthenticatedClient.get(`${resource}/protected`);
  },
  /** List invoices. @returns {Promise<import('axios').AxiosResponse>} */
  getInvoices() {
    return AuthenticatedScrumbleApiClient.get('stripe/invoices');
  },
  /** Current user's subscription info. @returns {Promise<import('axios').AxiosResponse>} */
  userSubscriptionInformation() {
    return AuthenticatedClient.get(`${resource}/user/subscription`);
  },
  /** Update a user's information. @param {object} payload @param {string} user user id/slug @returns {Promise<import('axios').AxiosResponse>} */
  updateUserInformation(payload, user) {
    return AuthenticatedScrumbleApiClient.put(`user/${user}`, payload);
  },
  /** Set an initial password from an email link. @param {object} payload @returns {Promise<import('axios').AxiosResponse>} */
  createUserPassword(payload) {
    return AuthenticatedClient.post(`${resource}/email_password`, payload);
  },
  /** Change the current user's password. @param {object} payload @returns {Promise<import('axios').AxiosResponse>} */
  updateUserPassword(payload) {
    return AuthenticatedScrumbleApiClient.put('password', payload);
  },
  /** Cancel a subscription. @param {string|number} id @returns {Promise<import('axios').AxiosResponse>} */
  cancelSubscription(id) {
    return AuthenticatedClient.get(`${resource}/subscription/cancel/${id}`);
  },
  /** Delete a subscription. @param {string|number} id @returns {Promise<import('axios').AxiosResponse>} */
  deleteSubscription(id) {
    return AuthenticatedClient.get(`${resource}/subscription/delete/${id}`);
  },
  /** Checkout info for a price. @param {string} priceId @returns {Promise<import('axios').AxiosResponse>} */
  getCheckoutInformation(priceId) {
    return AuthenticatedScrumbleApiClient.get(`user/subscription-checkout/${priceId}`);
  },
  /** Start a checkout. @param {object} payload @returns {Promise<import('axios').AxiosResponse>} */
  postCheckout(payload) {
    return Client.post(`${resource}/checkout`, payload);
  },
  /** List users (admin). @param {object} payload @returns {Promise<import('axios').AxiosResponse>} */
  getUsersAdministration(payload) {
    return AuthenticatedClient.get(`${resource}/users`);
  },
  /** List subscriptions (admin). @param {object} payload @returns {Promise<import('axios').AxiosResponse>} */
  getSubscriptionsAdministration(payload) {
    return AuthenticatedClient.get(`${resource}/subscribed`);
  },
  /** Resolved auth method ('direct' | 'proxy'). Synchronous — not a Promise. @returns {string} */
  getAuthMethod() {
    return authMethod;
  },
};
